import React, { useState, useMemo } from 'react';
import StackedLaborChart, { clipSeriesToRange, clipCategoriesToRange } from './StackedLaborChart';
import SegmentedControl from './SegmentedControl';
import { PLAN_VERSIONS } from '../../utils/constants';

/**
 * Available Labor section: per-cohort stacked area chart with a "Plan window
 * / Full timeline" toggle that clips the series to the planning interval span.
 *
 * For DYNAMIC_V2 plans, all cohorts are merged into a single combined series
 * since the per-cohort breakdown isn't meaningful in that mode.
 */
const AvailableLaborChart = ({ laborAvailabilityChart, intervals, planVersion }) => {
  const { perCohortAvailable, totalAvailable, netAvailable, categories } = laborAvailabilityChart || {};
  const isDynamicV2 = planVersion === PLAN_VERSIONS.DYNAMIC_V2;
  const [isAvailableExpanded, setIsAvailableExpanded] = useState(false);

  const planStartLabel = intervals?.[0]?.startLabel;
  const planEndLabel = intervals?.[intervals.length - 1]?.endLabel;
  const canTruncate = !!(planStartLabel && planEndLabel && categories?.length);

  // Per-cohort sweep-line series for STATIC plans.
  const availableSeries = useMemo(
    () => (perCohortAvailable || []).map((c, i) => ({ key: `cohort-${i}`, label: c.label, data: c.data })),
    [perCohortAvailable]
  );

  // DYNAMIC_V2: merge all sub-periods into a single combined series.
  const combinedSeries = useMemo(() => {
    if (!isDynamicV2 || !perCohortAvailable?.length) return [];
    const dataMap = new Map();
    perCohortAvailable.forEach((cohort) => {
      (cohort.data || []).forEach((pt) => {
        dataMap.set(pt.x, (dataMap.get(pt.x) || 0) + (pt.y || 0));
      });
    });
    const combined = Array.from(dataMap.entries())
      .map(([x, y]) => ({ x, y }))
      .sort((a, b) => (typeof a.x === 'number' ? a.x - b.x : String(a.x).localeCompare(String(b.x))));
    return [{ key: 'combined-available', label: 'Available HC', data: combined, color: '#0ea5e9' }];
  }, [isDynamicV2, perCohortAvailable]);

  const baseSeries = isDynamicV2 ? combinedSeries : availableSeries;
  if (!baseSeries.length) return null;

  const showFullTimeline = isAvailableExpanded || !canTruncate;

  const displayedSeries = showFullTimeline
    ? baseSeries
    : baseSeries.map((s) => ({
        ...s,
        data: clipSeriesToRange(s.data, categories, planStartLabel, planEndLabel)
      }));
  const displayedTotal = showFullTimeline
    ? totalAvailable || []
    : clipSeriesToRange(totalAvailable, categories, planStartLabel, planEndLabel);
  const displayedNet = isDynamicV2
    ? []
    : showFullTimeline
    ? netAvailable || []
    : clipSeriesToRange(netAvailable, categories, planStartLabel, planEndLabel);
  const displayedCategories = showFullTimeline
    ? categories || []
    : clipCategoriesToRange(categories, planStartLabel, planEndLabel);

  return (
    <StackedLaborChart
      title="Available Labor"
      series={displayedSeries}
      totalData={displayedTotal}
      netData={displayedNet}
      intervals={intervals}
      categories={displayedCategories}
      headerExtras={
        canTruncate && (
          <SegmentedControl
            value={isAvailableExpanded ? 'full' : 'plan'}
            options={[
              { value: 'plan', label: 'Plan window' },
              { value: 'full', label: 'Full timeline' }
            ]}
            onChange={(v) => setIsAvailableExpanded(v === 'full')}
            ariaLabel="Show plan window or full timeline"
          />
        )
      }
    />
  );
};

export default AvailableLaborChart;
